import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    HiOutlineBadgeCheck,
    HiOutlineExclamation,
    HiOutlineExclamationCircle,
    HiOutlineLightningBolt,
    HiOutlineShieldCheck,
    HiChevronRight,
} from 'react-icons/hi';
import BadgeStatus from '../../../../../atoms/BadgeStatus';
import StatusReasonModal from '../../../StatusReasonModal';
import SectionCard from '../../shared/SectionCard';
import { STUDIO_SECTIONS } from '../StudioNav';
import { isDraftId } from '../studioDraft';
import { StudioIssue } from '../studioIssues';
import { MessageCompositionStudio } from '../useMessageCompositionStudio';

const sectionLabel = (key: StudioIssue['section']) => STUDIO_SECTIONS.find((s) => s.key === key)?.label ?? key;

const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const IssueRow: React.FC<{ issue: StudioIssue; onOpen: () => void }> = ({ issue, onOpen }) => (
    <button
        type="button"
        onClick={onOpen}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-slate-50 dark:hover:bg-slate-700/50"
    >
        {issue.level === 'error' ? (
            <HiOutlineExclamationCircle className="text-red-500 shrink-0" size={18} />
        ) : (
            <HiOutlineExclamation className="text-amber-500 shrink-0" size={18} />
        )}
        <span className="flex-1 text-sm text-gray-700 dark:text-gray-200">{issue.message}</span>
        <span className="text-[11px] text-gray-400 shrink-0">{sectionLabel(issue.section)}</span>
        <HiChevronRight className="text-gray-300 shrink-0" size={16} />
    </button>
);

const ReviewSection: React.FC<{ studio: MessageCompositionStudio }> = ({ studio }) => {
    const { meta, issues, errors, usedBy, dirty } = studio;
    const [toggling, setToggling] = useState(false);
    if (!meta) return null;

    const warnings = issues.filter((i) => i.level === 'warning');
    const persisted = !isDraftId(meta.id);
    const active = meta.status === 'ACTIVE';
    const canToggle = persisted && !dirty && (active || !errors.length);

    const open = (issue: StudioIssue) => {
        if (issue.templateId) studio.setActiveTemplateId(issue.templateId);
        studio.setSection(issue.section);
    };

    return (
        <div className="flex flex-col gap-4">
            <div
                className={`rounded-2xl p-5 flex items-center gap-4 ${
                    errors.length
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900'
                        : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900'
                }`}
            >
                {errors.length ? (
                    <HiOutlineExclamationCircle className="text-red-500" size={36} />
                ) : (
                    <HiOutlineBadgeCheck className="text-emerald-500" size={36} />
                )}
                <div className="flex-1">
                    <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {errors.length ? `${errors.length} pendência(s) para resolver` : 'Tudo certo para usar'}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        {errors.length
                            ? 'Resolva os itens em vermelho para salvar a composição.'
                            : warnings.length
                              ? `${warnings.length} sugestão(ões) de melhoria, nenhuma impede o uso.`
                              : 'Conteúdo, envio e variáveis estão completos.'}
                    </div>
                </div>
            </div>

            {issues.length > 0 && (
                <SectionCard title="Checklist" description="Clique num item para ir até ele.">
                    <div className="-mx-3 flex flex-col">
                        {[...errors, ...warnings].map((issue, index) => (
                            <IssueRow key={index} issue={issue} onOpen={() => open(issue)} />
                        ))}
                    </div>
                </SectionCard>
            )}

            <SectionCard title="Status" description="Composições não são excluídas: são desativadas com um motivo." icon={<HiOutlineShieldCheck size={18} />}>
                <div className="flex items-center gap-3">
                    <BadgeStatus color={active ? 'green' : 'red'} text={active ? 'Ativo' : 'Inativo'} />
                    <span className="text-xs text-gray-500">
                        {persisted ? `Atualizado em ${formatDate(meta.updatedAt)}` : 'Ainda não salvo — será criado ativo.'}
                    </span>
                    <span className="flex-1" />
                    {persisted && (
                        <button
                            type="button"
                            disabled={!canToggle}
                            onClick={() => setToggling(true)}
                            title={dirty ? 'Salve as alterações antes' : !active && errors.length ? 'Resolva as pendências antes' : ''}
                            className={`px-3 py-1.5 rounded-md text-sm text-white disabled:opacity-40 disabled:cursor-not-allowed ${
                                active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-500 hover:bg-green-600'
                            }`}
                        >
                            {active ? 'Desativar' : 'Ativar'}
                        </button>
                    )}
                </div>
                {meta.statusReason && (
                    <p className="mt-3 text-xs text-gray-600 dark:text-gray-300 bg-slate-50 dark:bg-slate-900 rounded-lg px-3 py-2">
                        <span className="font-medium">Último motivo:</span> {meta.statusReason}
                    </p>
                )}
            </SectionCard>

            <SectionCard
                title="Onde é usado"
                description="Eventos que disparam esta composição. O roteamento é configurado pelo time de engenharia."
                icon={<HiOutlineLightningBolt size={18} />}
            >
                {usedBy.length ? (
                    <ul className="flex flex-col gap-1">
                        {usedBy.map((r) => (
                            <li key={r.id}>
                                <Link
                                    to={`/communication/routing/${r.id}`}
                                    className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                >
                                    <HiOutlineLightningBolt className="text-amber-500" size={16} />
                                    <span className="font-mono text-xs text-gray-800 dark:text-gray-100 flex-1">{r.event}</span>
                                    <span className="text-[11px] text-gray-400">{r.environments.join(' · ')}</span>
                                    <BadgeStatus color={r.enabled ? 'green' : 'gray'} text={r.enabled ? 'Ativo' : 'Inativo'} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-400">Nenhum evento dispara esta composição. Ela pode ser usada em envios manuais.</p>
                )}
            </SectionCard>

            <StatusReasonModal
                isOpen={toggling}
                title={active ? 'Desativar composição' : 'Ativar composição'}
                actionLabel={active ? 'Desativar' : 'Ativar'}
                onCancel={() => setToggling(false)}
                onConfirm={async (reason) => {
                    setToggling(false);
                    await studio.toggleStatus(reason);
                }}
            />
        </div>
    );
};

export default ReviewSection;
